import { useCallback, useRef, useState, type ReactNode } from 'react'
import { toast } from 'sonner'
import { apiClient, isDirectRecord } from '@indipia/shared'
import { useImmediateConfirm, isFixedPatchResponse, IMMEDIATE_CONFIRM_SUCCESS_MESSAGE } from './useImmediateConfirm'
import { useInvalidateSignals } from './useTaskSignals'
import { ForemanChangeReasonModal } from '../components/ForemanChangeReasonModal'
import { foremanChangeResultMessage } from '../components/ForemanChangeNotice'
import { changeConfirmedPartner, releasePartner, ForemanChangeError, type PartnerChangeReasonCode } from '../api/foremanApi'
import {
  ASSIGN_SOURCE, getPartnerSaveKind, mergeForemanAssigned, partnerStatusAfterAssign, partnerStatusAfterChange,
  partnerStatusAfterFixed, type PartnerStatus,
} from '../constants/partnerStatus'
import { FOREMAN_CHANGE_MESSAGE, foremanChangeErrorMessage } from '../constants/foremanSendMessage'
import type { SigongAssignUpdate } from '../views/SigongPopup'

// 공종 인라인 콤보·공종 편집 폼·시공반장 팝업 결과의 반장 배정 처리 공용 훅 (WP4 4-b).
// 분기는 간트 패널 저장과 같은 기준(getPartnerSaveKind): 해제(release)·확정 반장 변경(change, 사유 필수)·일반 배정(patch).
// 착공 D-2 이내 즉시 확정 확인(useImmediateConfirm)과 결합. 성공 토스트는 이 훅이, 오류 표시는 호출 화면이 onError 로 맡는다.

export interface ForemanTaskLike {
  taskId: number
  partnerStatus?: string | null
  assigneePartnerId?: number | null
  foremanName?: string | null
}

export interface ForemanPartnerLike {
  partnerId: number
  partnerName: string
  mobile?: string | null
  /** 연락 방식(SIF-3) — 'N'=결과만 직접등록(고르는 즉시 확정) */
  commYn?: string | null
}

/** 화면 상태에 병합할 값. 없는 키(undefined)는 기존 값을 유지한다. 연락처는 화면이 필드명(foremanContact/foremanPhone)에 맞춰 매핑. */
export interface ForemanStatePatch {
  foremanName: string | null
  foremanContact: string | null
  partnerStatus?: PartnerStatus
  sentDtm?: string | null
  respondedDtm?: string | null
  assigneePartnerId?: number | null
  assignSource?: string | null
}

interface UseForemanAssignArgs<T extends ForemanTaskLike> {
  partners: ForemanPartnerLike[]
  getTask: (taskId: number) => T | undefined
  applyPatch: (taskId: number, patch: ForemanStatePatch) => void
  onError: (msg: string) => void
  /** PATCH /schedule/tasks/{id} 요청 헤더 */
  headers: Record<string, string | undefined>
}

interface AskReasonState { previousName: string | null; resolve: (reason: PartnerChangeReasonCode | null) => void }

export function useForemanAssign<T extends ForemanTaskLike>({ partners, getTask, applyPatch, onError, headers }: UseForemanAssignArgs<T>) {
  const immediate = useImmediateConfirm()
  const invalidateSignals = useInvalidateSignals()   // 배정·변경·해제 성공 후 신호 재조회
  const [ask, setAsk] = useState<AskReasonState | null>(null)
  const askRef = useRef<AskReasonState | null>(null)
  askRef.current = ask

  /** 확정 반장 변경 사유 모달 — 취소하면 null */
  const askChangeReason = useCallback((previousName: string | null) => new Promise<PartnerChangeReasonCode | null>(resolve => {
    setAsk({ previousName, resolve })
  }), [])

  const closeAsk = (reason: PartnerChangeReasonCode | null) => {
    const a = askRef.current
    setAsk(null)
    a?.resolve(reason)
  }

  /**
   * 확정 반장을 다른 반장으로 바꾼다(sendNow=false) — 즉시 확정 확인 포함.
   * 즉시 확정 확인을 취소하면 false. 성공하면 토스트 후 applyPatch 하고 true. 오류는 던진다.
   */
  const changeConfirmed = async (taskId: number, partner: ForemanPartnerLike, previousName: string | null, reason: PartnerChangeReasonCode): Promise<boolean> => {
    const run = await immediate.run(ok => changeConfirmedPartner(taskId, {
      newPartnerId: partner.partnerId,
      reasonCode: reason,
      sendNow: false,
      ...(ok && { confirmImmediate: true }),
    }))
    if (run.cancelled) return false
    const result = run.value
    toast.success(result.fixed ? IMMEDIATE_CONFIRM_SUCCESS_MESSAGE : foremanChangeResultMessage(result, partner.partnerName, previousName))
    void invalidateSignals()
    applyPatch(taskId, {
      foremanName: partner.partnerName,
      foremanContact: partner.mobile ?? null,
      assigneePartnerId: partner.partnerId,
      assignSource: ASSIGN_SOURCE.MANUAL,
      ...partnerStatusAfterChange(result),
    })
    return true
  }

  /** 인라인 콤보 변경 — foremanName 이 null·빈 값이거나 목록에 없으면 '선택 안 함'(해제) */
  const assignPartner = async (taskId: number, foremanName: string | null): Promise<void> => {
    const partner = foremanName ? partners.find(p => p.partnerName === foremanName) : undefined
    try {
      const cur = getTask(taskId)
      // 목록 응답에 반장 id 가 없으면 이름으로 찾는다(삭제된 반장이면 null)
      const curId = cur?.assigneePartnerId
        ?? (cur?.foremanName ? (partners.find(p => p.partnerName === cur.foremanName)?.partnerId ?? null) : null)
      // 결과만 직접등록(SIF-3) 반장은 확정 공종이어도 사유 없이 PATCH — 서버가 그 반장으로 바로 확정한다
      const direct = isDirectRecord(partner?.commYn)
      const kind = getPartnerSaveKind({ partnerStatus: cur?.partnerStatus, assigneePartnerId: curId }, partner?.partnerId ?? null, direct)

      if (!partner) {
        // "선택 안 함" — 서버 반장을 해제한다(DELETE partners/{id}, FR-B-69). PATCH 의 null 은 변경 없음이라 해제되지 않는다.
        if (kind === 'release-blocked') { onError(FOREMAN_CHANGE_MESSAGE.releaseDenied); return }
        if (kind !== 'release' || curId == null) {
          if (cur?.foremanName) onError(FOREMAN_CHANGE_MESSAGE.unknown)   // 이름만 남은 삭제된 반장 — id 가 없어 해제할 수 없음
          return
        }
        await releasePartner(taskId, curId)
        void invalidateSignals()
        applyPatch(taskId, {
          foremanName: null, foremanContact: null, assigneePartnerId: null, assignSource: null,
          ...partnerStatusAfterAssign(false),
        })
        return
      }

      if (kind === 'none') return

      if (kind === 'change') {
        // 확정 반장 변경 — 사유 필수, PATCH 로 우회하지 않는다(간트 패널 저장과 같은 partner-changes)
        const reason = await askChangeReason(cur?.foremanName ?? null)
        if (!reason) return
        await changeConfirmed(taskId, partner, cur?.foremanName ?? null, reason)
        return
      }

      const run = await immediate.run(ok => apiClient.patch(`/schedule/tasks/${taskId}`, {
        assigneePartnerId: partner.partnerId,
        updatePredecessor: false,
        ...(ok && { confirmImmediate: true }),
      }, { headers }))
      if (run.cancelled) return   // 취소 — 저장하지 않음(콤보박스는 기존 반장 그대로)
      const fixed = isFixedPatchResponse(run.value)
      // 착공 임박 즉시 확정만 안내(반장에게 알림이 감). 결과만 직접등록 확정은 알리지 않는다
      if (fixed && !direct) toast.success(IMMEDIATE_CONFIRM_SUCCESS_MESSAGE)
      void invalidateSignals()
      // 전체 재조회 대신 이 행만 국소 갱신 — 반장이 바뀌었으므로 이전 응답(확인 상태)도 함께 초기화한다.
      applyPatch(taskId, {
        foremanName: partner.partnerName,
        foremanContact: partner.mobile ?? null,
        assigneePartnerId: partner.partnerId,
        assignSource: ASSIGN_SOURCE.MANUAL,
        ...(fixed ? partnerStatusAfterFixed() : partnerStatusAfterAssign(true)),
      })
    } catch (err) {
      onError(err instanceof ForemanChangeError ? foremanChangeErrorMessage(err) : '협력업체 저장 중 오류가 발생했습니다.')
    }
  }

  /** 시공반장 팝업(SigongPopup) onAssigned 수신 — 바뀐 공종 한 건만 국소 병합 */
  const applyAssigned = (u: SigongAssignUpdate): void => {
    // 신호 무효화는 결과를 올려 보낸 SigongPopup 이 한다(onAssigned 래퍼)
    const merged = mergeForemanAssigned<Partial<ForemanStatePatch>>({}, u)
    const { partnerStatus, sentDtm, respondedDtm, assigneePartnerId, assignSource } = merged
    applyPatch(u.taskId, {
      foremanName: u.foremanName,
      foremanContact: u.foremanContact,
      ...(partnerStatus !== undefined && { partnerStatus, sentDtm, respondedDtm }),
      ...(assigneePartnerId !== undefined && { assigneePartnerId }),
      ...(assignSource !== undefined && { assignSource }),
    })
  }

  const modal: ReactNode = (
    <>
      {immediate.modal}
      <ForemanChangeReasonModal
        open={ask != null}
        previousName={ask?.previousName ?? null}
        onConfirm={r => closeAsk(r)}
        onCancel={() => closeAsk(null)}
      />
    </>
  )

  return { assignPartner, applyAssigned, changeConfirmed, askChangeReason, immediateRun: immediate.run, modal }
}
